"use client";

import { type ComponentProps } from "react";
import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../ui/form";

interface ValidatedInputProps extends ComponentProps<typeof Input> {
  name: string;
  label: string;
  disabled?: boolean;
}

export const ValidatedInput = ({
  name,
  label,
  placeholder,
  type,
  disabled,
  ...rest
}: ValidatedInputProps) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      disabled={disabled}
      render={({ field }) => (
        <FormItem id={name}>
          <FormLabel htmlFor={name}>{label}</FormLabel>
          <FormControl>
            <Input
              {...field}
              {...rest}
              aria-label={label}
              id={name}
              placeholder={placeholder}
              type={type}
            />
          </FormControl>
          <FormMessage className="min-h-5"> </FormMessage>
        </FormItem>
      )}
    />
  );
};

interface ValidatedTextareaProps extends ComponentProps<typeof Textarea> {
  name: string;
  label: string;
  disabled?: boolean;
}

export const ValidatedTextarea = ({
  name,
  label,
  disabled,
  ...rest
}: ValidatedTextareaProps) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      disabled={disabled}
      render={({ field }) => (
        <FormItem id={name}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea {...field} disabled={disabled} {...rest} />
          </FormControl>
          <FormMessage className="min-h-5"> </FormMessage>
        </FormItem>
      )}
    />
  );
};

interface ValidatedSelectProps {
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  options: { value: string; label: string }[];
  multiple?: boolean;
  className?: string;
  disabled?: boolean;
}

export function ValidatedSelect({
  name,
  label,
  className,
  placeholder,
  options,
  disabled,
}: ValidatedSelectProps) {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      disabled={disabled}
      render={({ field }) => (
        <FormItem id={name}>
          <FormLabel htmlFor={name}>{label}</FormLabel>
          <FormControl>
            <Select
              onValueChange={field.onChange}
              value={field.value as string}
              disabled={disabled}
            >
              <SelectTrigger
                className={cn(
                  "h-input-md w-full rounded-lg px-4 py-1 text-input-md",
                  className,
                )}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage className="min-h-5"> </FormMessage>
        </FormItem>
      )}
    />
  );
}
